import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiCheckCircle, FiXCircle, FiAlertCircle, FiBarChart2, FiClock, FiShield } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import examService from "../../services/examService";

const GRADE_COLORS = {
    O: { bg: "#dcfce7", color: "#15803d" },
    "A+": { bg: "#e8f1f8", color: "#002747" },
    A: { bg: "#e8f1f8", color: "#003b67" },
    "B+": { bg: "#fef9c3", color: "#92400e" },
    B: { bg: "#fef3c7", color: "#d97706" },
    F: { bg: "#fee2e2", color: "#dc2626" },
    Ab: { bg: "#f3f4f6", color: "#6b7280" },
    "–": { bg: "#f3f4f6", color: "#6b7280" },
};

const RESULT_ICON = {
    Pass: <FiCheckCircle size={13} color="#16a34a" />,
    Fail: <FiXCircle size={13} color="#dc2626" />,
    Absent: <FiAlertCircle size={13} color="#6b7280" />,
};

const normalizeResultSheet = (payload) => {
    const exam = payload?.exam && !Array.isArray(payload.exam)
        ? { ...payload.exam, subjects: Array.isArray(payload.exam.subjects) ? payload.exam.subjects : [] }
        : null;

    return {
        exam,
        students: Array.isArray(payload?.data) ? payload.data : [],
    };
};

export default function ResultEntry() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useApp();
    const isAdmin = ["admin", "superadmin"].includes(user?.role);

    const [exam, setExam] = useState(null);
    const [rows, setRows] = useState([]);  // [{ student, marks: {subjectName: {marksObtained, isAbsent}}, remarks, approvalStatus, _computed }]
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const subjectList = Array.isArray(exam?.subjects) ? exam.subjects : [];

    // ── Load sheet ───────────────────────────────────────────────────────────

    const loadSheet = useCallback(async () => {
        setLoading(true);
        try {
            const res = await examService.getResultSheet(id);
            const { exam: examData, students } = normalizeResultSheet(res);
            if (!examData) throw new Error("Result sheet could not be loaded.");
            setExam(examData);

            // Build editable row state from API data
            const built = students.map((student) => {
                const marksMap = {};
                examData.subjects.forEach((sub) => {
                    const existing = student.result?.marks?.find((m) => m.subjectName === sub.name);
                    marksMap[sub.name] = {
                        marksObtained: existing ? existing.marksObtained : "",
                        isAbsent: existing ? existing.isAbsent : false,
                    };
                });
                return {
                    student,
                    marks: marksMap,
                    remarks: student.result?.remarks || "",
                    approvalStatus: student.result?.approvalStatus || "Pending",
                };
            });
            setRows(built);
        } catch (err) {
            toast.error(err.message);
            navigate("/exams");
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { loadSheet(); }, [loadSheet]);

    // ── Cell change handlers ──────────────────────────────────────────────────

    const updateMark = (rowIdx, subjectName, field, value) => {
        setRows((prev) =>
            prev.map((row, i) => {
                if (i !== rowIdx) return row;
                // Teachers cannot edit approved or published rows
                if (!isAdmin && (row.approvalStatus === "Approved" || isPublished)) return row;
                return {
                    ...row,
                    marks: {
                        ...row.marks,
                        [subjectName]: { ...row.marks[subjectName], [field]: value },
                    },
                };
            }),
        );
    };

    const toggleAbsent = (rowIdx, subjectName) => {
        setRows((prev) =>
            prev.map((row, i) => {
                if (i !== rowIdx) return row;
                if (!isAdmin && (row.approvalStatus === "Approved" || isPublished)) return row;
                const current = row.marks[subjectName];
                return {
                    ...row,
                    marks: {
                        ...row.marks,
                        [subjectName]: { ...current, isAbsent: !current.isAbsent, marksObtained: "" },
                    },
                };
            }),
        );
    };

    const updateRemarks = (rowIdx, value) => {
        setRows((prev) =>
            prev.map((row, i) => {
                if (i !== rowIdx) return row;
                if (!isAdmin && (row.approvalStatus === "Approved" || isPublished)) return row;
                return { ...row, remarks: value };
            })
        );
    };

    // ── Live preview: per-row computed totals ────────────────────────────────

    const computeRow = (row) => {
        if (!exam) return { totalObtained: 0, totalMax: 0, pct: 0, grade: "–", result: "–" };
        let totalObtained = 0;
        let totalMax = 0;
        let anyFail = false;
        let allAbsent = true;

        subjectList.forEach((sub) => {
            const cell = row.marks[sub.name] || {};
            totalMax += sub.maxMarks;
            if (!cell.isAbsent) {
                allAbsent = false;
                const obt = Number(cell.marksObtained) || 0;
                totalObtained += obt;
                if (obt < sub.passingMarks) anyFail = true;
            }
        });

        if (allAbsent) return { totalObtained: 0, totalMax, pct: 0, grade: "Ab", result: "Absent" };
        if (anyFail) return { totalObtained, totalMax, pct: Number(((totalObtained / totalMax) * 100).toFixed(1)), grade: "F", result: "Fail" };

        const pct = totalMax > 0 ? Number(((totalObtained / totalMax) * 100).toFixed(1)) : 0;
        const grade = pct >= 75 ? "O" : pct >= 65 ? "A+" : pct >= 55 ? "A" : pct >= 50 ? "B+" : pct >= 45 ? "B" : "F";
        const result = grade === "F" ? "Fail" : "Pass";
        return { totalObtained, totalMax, pct, grade, result };
    };

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        setSaving(true);
        try {
            const targetRows = !isAdmin
                ? rows.filter((r) => r.approvalStatus !== "Approved")
                : rows;

            if (targetRows.length === 0) {
                toast.error("All results are already approved and locked.");
                setSaving(false);
                return;
            }

            const entries = targetRows.map((row) => ({
                studentId: row.student._id,
                remarks: row.remarks,
                marks: subjectList.map((sub) => ({
                    subjectName: sub.name,
                    marksObtained: Number(row.marks[sub.name]?.marksObtained) || 0,
                    isAbsent: row.marks[sub.name]?.isAbsent || false,
                })),
            }));

            const res = await examService.saveResults(id, entries);
            toast.success(res.message || "Results saved successfully.");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to save results");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading result sheet...</div>;
    if (!exam) return null;

    const colWidth = Math.max(100, Math.floor(600 / Math.max(subjectList.length, 1)));
    const isPublished = exam.status === "Results Published";
    const allApproved = rows.length > 0 && rows.every((r) => r.approvalStatus === "Approved" || isPublished);
    const approvedCount = rows.filter((r) => r.approvalStatus === "Approved").length;

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>{exam.title} — Result Entry</h1>
                        {isPublished ? (
                            <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "2px 9px", borderRadius: 99, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <FiCheckCircle size={12} /> Published
                            </span>
                        ) : (
                            <span style={{ background: "#fef9c3", color: "#92400e", border: "1px solid #fde047", padding: "2px 9px", borderRadius: 99, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <FiClock size={12} /> Pending Admin Approval
                            </span>
                        )}
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {exam.courseAdmitted} · {exam.courseYear} · {exam.examType}
                        {exam.examDate ? ` · ${exam.examDate}` : ""}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/exams")}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => navigate(`/exams/${id}/results`)}>
                        <FiBarChart2 size={14} /> View Report & Approvals
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving || (!isAdmin && (allApproved || isPublished))}
                    >
                        <FiSave size={14} /> {saving ? "Saving..." : !isAdmin && allApproved ? "All Approved (Locked)" : "Save Results"}
                    </button>
                </div>
            </div>

            {/* ── Informational Banners ── */}
            {!isAdmin ? (
                allApproved ? (
                    <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#166534", display: "flex", alignItems: "center", gap: 8 }}>
                        <FiCheckCircle size={16} style={{ flexShrink: 0 }} />
                        <div>
                            <strong>Locked:</strong> All student results for this exam are <strong>Approved by Admin</strong>. Results cannot be modified by teachers.
                        </div>
                    </div>
                ) : approvedCount > 0 ? (
                    <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#1e40af", display: "flex", alignItems: "center", gap: 8 }}>
                        <FiClock size={16} style={{ flexShrink: 0 }} />
                        <div>
                            <strong>Teacher Notice:</strong> {approvedCount} student result(s) have been approved by Admin and are locked. You can only enter or update marks for pending students.
                        </div>
                    </div>
                ) : (
                    <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#1e40af", display: "flex", alignItems: "center", gap: 8 }}>
                        <FiShield size={16} style={{ flexShrink: 0 }} />
                        <div>
                            <strong>Teacher Notice:</strong> Marks entered and saved here will be stored in <strong>Pending Approval</strong> state. Once the Admin approves results, they become locked.
                        </div>
                    </div>
                )
            ) : isPublished ? (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#166534", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiCheckCircle size={16} style={{ flexShrink: 0 }} />
                    <div>
                        <strong>Published Exam:</strong> These results are currently published to students. Any changes saved by Admin will update the live student marksheets immediately.
                    </div>
                </div>
            ) : (
                <div style={{ background: "#fefce8", border: "1px solid #fef08a", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#854d0e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiClock size={16} style={{ flexShrink: 0 }} />
                    <div>
                        <strong>Admin Notice:</strong> Results are in <strong>Draft / Pending Approval</strong>. Once teachers have entered marks, navigate to <strong>View Report & Approvals</strong> to approve students and publish results.
                    </div>
                </div>
            )}

            {/* ── No students warning ── */}
            {rows.length === 0 && (
                <div className="card">
                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                        No active students found for {exam.courseAdmitted} {exam.courseYear}. Add students first.
                    </div>
                </div>
            )}

            {/* ── Entry Table ── */}
            {rows.length > 0 && (
                <div className="card">
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ minWidth: 800 }}>
                            <thead>
                                <tr>
                                    <th style={{ minWidth: 40 }}>#</th>
                                    <th style={{ minWidth: 200 }}>Student</th>
                                    {subjectList.map((sub) => (
                                        <th key={sub.name} style={{ minWidth: colWidth, textAlign: "center" }}>
                                            <div>{sub.name}</div>
                                            <div style={{ fontWeight: 400, fontSize: 11, marginTop: 1 }}>
                                                Max: {sub.maxMarks} · Pass: {sub.passingMarks}
                                            </div>
                                        </th>
                                    ))}
                                    <th style={{ minWidth: 110, textAlign: "center" }}>Total / %</th>
                                    <th style={{ minWidth: 80, textAlign: "center" }}>Grade</th>
                                    <th style={{ minWidth: 80, textAlign: "center" }}>Result</th>
                                    <th style={{ minWidth: 90, textAlign: "center" }}>Status</th>
                                    <th style={{ minWidth: 140 }}>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, rowIdx) => {
                                    const computed = computeRow(row);
                                    const gc = GRADE_COLORS[computed.grade] || GRADE_COLORS["–"];
                                    const isApproved = row.approvalStatus === "Approved";
                                    const isRowLocked = !isAdmin && (isApproved || isPublished);

                                    return (
                                        <tr key={row.student._id} style={{ background: isRowLocked ? "#f8fafc" : "#fff" }}>
                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{rowIdx + 1}</td>
                                            <td>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>{row.student.fullName}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                    {row.student.enrollmentNo}
                                                    {row.student.rollNo ? ` · Roll ${row.student.rollNo}` : ""}
                                                </div>
                                            </td>

                                            {/* Subject cells */}
                                            {subjectList.map((sub) => {
                                                const cell = row.marks[sub.name] || {};
                                                const obt = Number(cell.marksObtained) || 0;
                                                const isFail = !cell.isAbsent && cell.marksObtained !== "" && obt < sub.passingMarks;
                                                return (
                                                    <td key={sub.name} style={{ textAlign: "center", padding: "0.5rem" }}>
                                                        {cell.isAbsent ? (
                                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                                                                <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>ABSENT</span>
                                                                {!isRowLocked && (
                                                                    <button type="button"
                                                                        style={{ fontSize: 10, color: "#003b67", background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}
                                                                        onClick={() => toggleAbsent(rowIdx, sub.name)}
                                                                    >undo</button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    max={sub.maxMarks}
                                                                    disabled={isRowLocked}
                                                                    value={cell.marksObtained}
                                                                    onChange={(e) => updateMark(rowIdx, sub.name, "marksObtained", e.target.value)}
                                                                    style={{
                                                                        width: 68,
                                                                        textAlign: "center",
                                                                        padding: "4px 6px",
                                                                        border: `1px solid ${isFail ? "#dc2626" : "var(--border)"}`,
                                                                        borderRadius: "var(--radius)",
                                                                        fontSize: 13,
                                                                        background: isRowLocked ? "#f1f5f9" : isFail ? "#fff1f1" : "#fff",
                                                                        color: isRowLocked ? "#475569" : "inherit",
                                                                        outline: "none",
                                                                        fontFamily: "inherit",
                                                                    }}
                                                                />
                                                                {!isRowLocked && (
                                                                    <button type="button"
                                                                        style={{ fontSize: 10, color: "#6b7280", background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}
                                                                        onClick={() => toggleAbsent(rowIdx, sub.name)}
                                                                    >mark AB</button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}

                                            {/* Total / % */}
                                            <td style={{ textAlign: "center", fontSize: 13 }}>
                                                <div style={{ fontWeight: 700 }}>
                                                    {computed.result === "Absent" ? "–" : `${computed.totalObtained}/${computed.totalMax}`}
                                                </div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                    {computed.result === "Absent" ? "Absent" : `${computed.pct}%`}
                                                </div>
                                            </td>

                                            {/* Grade */}
                                            <td style={{ textAlign: "center" }}>
                                                <span style={{ background: gc.bg, color: gc.color, padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700 }}>
                                                    {computed.grade}
                                                </span>
                                            </td>

                                            {/* Result */}
                                            <td style={{ textAlign: "center" }}>
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 12.5, fontWeight: 600 }}>
                                                    {RESULT_ICON[computed.result]} {computed.result}
                                                </div>
                                            </td>

                                            {/* Approval status badge */}
                                            <td style={{ textAlign: "center" }}>
                                                {isApproved ? (
                                                    <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "2px 7px", borderRadius: 99, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                                                        {!isAdmin && "🔒 " }Approved
                                                    </span>
                                                ) : (
                                                    <span style={{ background: "#fef9c3", color: "#a16207", border: "1px solid #fde047", padding: "2px 7px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                                                        Pending
                                                    </span>
                                                )}
                                            </td>

                                            {/* Remarks */}
                                            <td>
                                                <input
                                                    value={row.remarks}
                                                    disabled={isRowLocked}
                                                    onChange={(e) => updateRemarks(rowIdx, e.target.value)}
                                                    placeholder={isRowLocked ? "Locked" : "Optional"}
                                                    style={{ width: "100%", padding: "4px 6px", border: "1px solid var(--border)", borderRadius: "var(--radius)", fontSize: 12.5, fontFamily: "inherit", outline: "none", background: isRowLocked ? "#f1f5f9" : "#fff" }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Footer save bar ── */}
                    <div style={{ padding: "0.85rem 1.25rem", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
                        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                            {rows.length} student{rows.length !== 1 ? "s" : ""} · {subjectList.length} subject{subjectList.length !== 1 ? "s" : ""}
                        </span>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving || (!isAdmin && (allApproved || isPublished))}
                        >
                            <FiSave size={14} /> {saving ? "Saving..." : !isAdmin && allApproved ? "All Approved (Locked)" : "Save Results"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
